import React, { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Lock, ArrowLeft, Loader2, CheckCircle2, AlertTriangle } from 'lucide-react';

export default function ResetPasswordPage() {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const token = searchParams.get('token');

    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [verifying, setVerifying] = useState(true);
    const [tokenValid, setTokenValid] = useState(false);
    const [email, setEmail] = useState('');
    const [success, setSuccess] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        const verifyToken = async () => {
            if (!token) {
                setVerifying(false);
                return;
            }

            try {
                const response = await axios.get(`/api/auth/verify-reset-token?token=${token}`);
                setTokenValid(true);
                setEmail(response.data.email);
            } catch (err) {
                setTokenValid(false);
            } finally {
                setVerifying(false);
            }
        };

        verifyToken();
    }, [token]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        if (password !== confirmPassword) {
            setError('Passwords do not match');
            return;
        }

        if (password.length < 6) {
            setError('Password must be at least 6 characters');
            return;
        }

        setLoading(true);

        try {
            await axios.post('/api/auth/reset-password', {
                token,
                new_password: password
            });
            setSuccess(true);
        } catch (err) {
            setError(err.response?.data?.detail || 'Failed to reset password. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    if (verifying) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-[#F8F7FC]">
                <div className="text-center">
                    <Loader2 className="w-12 h-12 animate-spin text-zinc-400 mx-auto mb-4" />
                    <p className="text-zinc-600 font-medium">Verifying reset link...</p>
                </div>
            </div>
        );
    }

    if (!token || !tokenValid) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-[#F8F7FC] p-8">
                <div className="w-full max-w-md">
                    <div className="brutalist-card p-8 text-center" data-testid="invalid-token-card">
                        <div className="w-16 h-16 rounded-full bg-red-100 border-2 border-zinc-950 flex items-center justify-center mx-auto mb-6">
                            <AlertTriangle className="w-8 h-8 text-red-600" />
                        </div>
                        <h1 className="text-2xl font-black tracking-tight text-zinc-950 mb-4">Invalid or Expired Link</h1>
                        <p className="text-zinc-600 mb-8">
                            This password reset link is invalid or has expired. Please request a new one.
                        </p>
                        <Link
                            to="/forgot-password"
                            className="brutalist-btn-primary inline-flex items-center gap-2"
                            data-testid="request-new-link"
                        >
                            Request New Link
                        </Link>
                    </div>
                </div>
            </div>
        );
    }

    if (success) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-[#F8F7FC] p-8">
                <div className="w-full max-w-md">
                    <div className="brutalist-card p-8 text-center" data-testid="reset-success-card">
                        <div className="w-16 h-16 rounded-full bg-green-100 border-2 border-zinc-950 flex items-center justify-center mx-auto mb-6">
                            <CheckCircle2 className="w-8 h-8 text-green-600" />
                        </div>
                        <h1 className="text-2xl font-black tracking-tight text-zinc-950 mb-4">Password Reset!</h1>
                        <p className="text-zinc-600 mb-8">
                            Your password has been successfully reset. You can now sign in with your new password.
                        </p>
                        <Link
                            to="/login"
                            className="brutalist-btn-primary inline-flex items-center gap-2"
                            data-testid="go-to-login-link"
                        >
                            Sign In
                        </Link>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen flex items-center justify-center bg-[#F8F7FC] p-8">
            <div className="w-full max-w-md">
                <div className="mb-8">
                    <Link to="/login" className="inline-flex items-center gap-2 text-zinc-600 hover:text-zinc-950 mb-6">
                        <ArrowLeft className="w-4 h-4" />
                        Back to Sign In
                    </Link>
                    <h1 className="text-3xl font-black tracking-tight text-zinc-950 mb-2">Set New Password</h1>
                    <p className="text-zinc-600">Enter a new password for <span className="font-semibold">{email}</span></p>
                </div>

                <div className="brutalist-card p-8" data-testid="reset-password-card">
                    <form onSubmit={handleSubmit} className="space-y-6">
                        {error && (
                            <div className="p-4 bg-red-50 border-2 border-red-500 rounded-lg" data-testid="reset-password-error">
                                <p className="text-red-700 font-medium">{error}</p>
                            </div>
                        )}

                        <div>
                            <label className="block text-sm font-semibold text-zinc-700 mb-2">
                                New Password
                            </label>
                            <div className="relative">
                                <Lock className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-zinc-400" />
                                <input
                                    type="password"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    className="brutalist-input has-leading-icon"
                                    placeholder="••••••••"
                                    required
                                    minLength={6}
                                    data-testid="reset-password-input"
                                />
                            </div>
                        </div>

                        <div>
                            <label className="block text-sm font-semibold text-zinc-700 mb-2">
                                Confirm New Password
                            </label>
                            <div className="relative">
                                <Lock className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-zinc-400" />
                                <input
                                    type="password"
                                    value={confirmPassword}
                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                    className="brutalist-input has-leading-icon"
                                    placeholder="••••••••"
                                    required
                                    minLength={6}
                                    data-testid="reset-confirm-password-input"
                                />
                            </div>
                        </div>

                        <button
                            type="submit"
                            disabled={loading}
                            className="brutalist-btn-primary w-full flex items-center justify-center gap-2"
                            data-testid="reset-password-submit-button"
                        >
                            {loading ? (
                                <>
                                    <Loader2 className="w-5 h-5 animate-spin" />
                                    Resetting...
                                </>
                            ) : (
                                'Reset Password'
                            )}
                        </button>
                    </form>
                </div>
            </div>
        </div>
    );
}
